// The Productivity Chart's two thin context bands - Production Groups and Feed Mixes - and the Note
// and Alarm marks that ride the first of them. All four are ordinary Highcharts series rather than
// an HTML overlay, so none of them can drift out of alignment with the plot below through a zoom,
// navigator drag or window resize.

import { i18n } from '@/plugins/internationalization/i18n';
import colors from '@/plugins/tailwind';
import type { AlarmMarker, FeedMixBlock, NoteMarker, ProductivityProductionGroup } from '@/types/SectionLiveKpi';
import { productionGroupEndMs } from './productivityAxis';
import { PRODUCTION_GROUP_BAND_HEIGHT_PX } from './productivityBands';
import { CHART_CONTEXT_BAND_COLORS, CHART_EVENT_COLORS } from './productivityColors';
import { escapeHtml, productivityInstant } from './productivityFormatting';
import { PRODUCTIVITY_AXIS } from './productivityMetrics';

// Both context bands ride a 0..1 axis of their own. Notes and Alarms get a row each rather than
// sharing one: a Section with a long run of water Alarms and a long run of Notes puts forty pins on
// a single line, where they touch end to end and neither run can be told from the other.
//
// The Feed Mix bar keeps the middle of its own band, where an xrange bar's full height stays inside
// it. The Production Group band is shared with the two pin rows, so its bar sits low and the rows stack
// above it - positioned in pixels and converted here, because a fraction eyeballed against one band
// height stops clearing the bar the moment that height changes, which is exactly how the Note row
// came to be drawn on top of the Production Group bar.
const CONTEXT_AXIS_MIDDLE = 0.5;

// Three rows on the Production Group band's 0..1 axis, packed as tightly as the marks allow: Alarm
// marks, Note pins, then the Production Group bar. Measured against PRODUCTION_GROUP_BAND_HEIGHT_PX (64px),
// with a flag drawn *downwards* from its anchor and occupying about 21px, and a scatter marker
// centred on its own point:
//
//   band top   +6   Alarm marks  centred on 6, so 0..12
//              +13  Note pins   13..34
//              +46  bar         36..56   (pointWidth 20, centred on 46)
//   band floor +64
//
// The two marks did briefly share one lane, which saved a further 8px and cost more than it saved:
// at twelve months the Notes sat directly on top of the Alarm triangles and hid them. A triangle is
// 12px and a pin 21px, so staggering them by seven costs almost nothing and collides never.
//
// Derived from the band height rather than written out as the fractions it works out to, so a row
// cannot go on claiming a position the band no longer has: a fraction eyeballed against one height
// stops clearing the bar the moment that height changes, and a bar half outside its band is drawn
// on top of the Feed Mix bar below it with its own label silently cropped away.
const ALARM_ROW_Y = 1 - 6 / PRODUCTION_GROUP_BAND_HEIGHT_PX;
const NOTE_ROW_Y = 1 - 13 / PRODUCTION_GROUP_BAND_HEIGHT_PX;
const PRODUCTION_GROUP_BAR_Y = 1 - 46 / PRODUCTION_GROUP_BAND_HEIGHT_PX;

// A context bar's name is drawn inside the bar, so it has to be cut to the bar. Highcharts will
// happily overflow it across every neighbour to the right, which on a range holding a dozen short
// feed-mix stretches renders as one illegible run of overlapping text.
//
// Measured in characters against the 11px bold the two context bands use, rather than by asking the
// renderer: the label formatter runs before the text is laid out, so no measured width exists yet.
const CONTEXT_LABEL_CHAR_PX = 6.2;
const CONTEXT_LABEL_INSET_PX = 20;
// Below this there is no room for a name that says anything, so the bar carries none at all - it is
// still a visible block, and the readout row names what is being fed at the cursor.
const CONTEXT_LABEL_MIN_CHARS = 4;

function clipContextLabel(name: string, barWidthPx: number | undefined): string {
    if (name === '') return '';
    const chars = Math.floor(((barWidthPx ?? 0) - CONTEXT_LABEL_INSET_PX) / CONTEXT_LABEL_CHAR_PX);
    if (chars < CONTEXT_LABEL_MIN_CHARS) return '';
    return name.length <= chars ? name : `${name.slice(0, chars - 1).trimEnd()}…`;
}

// Shared by both context bands: left-aligned inside the bar rather than centred, because a bar
// spanning the whole axis puts a centred name in the middle of nothing, where it reads as a label
// for the data below it instead of for the bar it sits on.
function contextBandDataLabels(color: string): Record<string, unknown> {
    return {
        enabled: true,
        align: 'left',
        inside: true,
        x: 12,
        // Never let a name escape its own bar, whatever the clipping above concludes.
        overflow: 'allow',
        crop: true,
        formatter(this: { point: { name?: string; shapeArgs?: { width?: number } } }): string {
            return clipContextLabel(this.point.name ?? '', this.point.shapeArgs?.width);
        },
        style: { fontSize: '11px', fontWeight: '700', color, textOutline: 'none' },
    };
}

// Each Production Group as one point on its own `xrange` series, in the chart's thin context
// band - drawn by Highcharts as part of the normal render cycle rather than an
// HTML overlay, so the band cannot drift out of alignment with the plot below it through a zoom,
// navigator drag or window resize. An open productionGroup's bar reaches to `nowMs`, since an `xrange`
// point requires a finite `x2`.
export function buildProductionGroupSeries(productionGroups: ProductivityProductionGroup[], nowMs: number) {
    return {
        type: 'xrange',
        name: i18n.global.t('views.monitoring.activeGroups.modals.productivity.productionGroupBand') as string,
        // xrange defaults to colorByPoint, which hands every bar the next palette colour and
        // ignores the series colour entirely - leaving this band and the Feed Mix band below it
        // rendered in exactly the same hue, and each bar in a different one from its neighbour.
        colorByPoint: false,
        // A tinted chip with its own outline rather than a solid slab: this band is context for the
        // data below it, and a saturated full-width bar reads as the loudest thing on the chart
        // when it is the quietest thing on it. Matches the pale-fill/dark-ink chips the Section
        // table already uses for the same Production Group.
        color: CHART_CONTEXT_BAND_COLORS.productionGroup.fill,
        borderColor: CHART_CONTEXT_BAND_COLORS.productionGroup.border,
        borderWidth: 1,
        yAxis: PRODUCTIVITY_AXIS.productionGroups,
        pointWidth: 20,
        borderRadius: 10,
        showInLegend: false,
        data: productionGroups.map((productionGroup) => ({
            x: Date.parse(productionGroup.start),
            x2: productionGroupEndMs(productionGroup, nowMs),
            // Low in the band, not on its floor. An xrange bar is drawn centred on its y value
            // with pointWidth as its height, so a bar sitting on the floor has half its height
            // below the band and is clipped away to a hairline - and sitting at the middle it
            // collides with the pin rows that share this band.
            y: PRODUCTION_GROUP_BAR_Y,
            name: productionGroup.name,
        })),
        dataLabels: contextBandDataLabels(CHART_CONTEXT_BAND_COLORS.productionGroup.text),
    };
}

// Each Feed Mix block as one point on its own `xrange` series, in the chart's second thin
// context band, right below the Production Group one - same rationale as that band:
// Highcharts owns the geometry, so the band cannot drift out of alignment through a zoom,
// navigator drag or window resize. A block spanning a Production Group boundary (a Feed Mix commonly
// outlives one Production Group) draws as a single unbroken bar regardless - this band is oblivious
// to where the Production Group band's own boundaries fall. The backend terminates every block - the
// last one at its Production Group's end, or at the range's - so the `nowMs` fallback below only
// guards a block that arrives without one.
export const FEED_MIX_SERIES_ID = 'productivityFeedMixes';
export const FEED_MIX_BORDER_COLOR = CHART_CONTEXT_BAND_COLORS.feedMix.border;

export function buildFeedMixSeries(feedMixes: FeedMixBlock[], nowMs: number) {
    return {
        type: 'xrange',
        id: FEED_MIX_SERIES_ID,
        name: i18n.global.t('views.monitoring.activeGroups.modals.productivity.feedMixBand') as string,
        yAxis: PRODUCTIVITY_AXIS.feedMixes,
        // Opted back in against the chart-wide opt-out, and only on this series rather than on the
        // `xrange` type, which the Production Group band above also uses. A mix name is routinely longer
        // than the block it is drawn in - "BAT Starter Bla 80 High UL DK, gekr. (19.11.2025)" in a
        // few weeks' width - so the label is truncated or dropped entirely and hover is the only
        // way to read it. Sticky tracking off for the same reason as the Alarm marks: these points
        // are rectangles with real hit areas, so the tooltip belongs to the block under the cursor.
        // Left on, this series joins the pool Highcharts picks a hovered point from by distance and
        // swallows the Note pins' own hover, which sit directly above it.
        enableMouseTracking: true,
        stickyTracking: false,
        pointWidth: 20,
        borderRadius: 10,
        showInLegend: false,
        // Slate, not a second blue: the Feed Mix band is context for the Production Group band above
        // it, and has to be distinguishable from it at a glance rather than merely adjacent. Same
        // tinted-chip treatment, so the two bands read as one family.
        colorByPoint: false,
        color: CHART_CONTEXT_BAND_COLORS.feedMix.fill,
        borderColor: FEED_MIX_BORDER_COLOR,
        borderWidth: 1,
        data: feedMixes.map((block) => ({
            x: productivityInstant(block.start),
            x2: block.end !== null ? productivityInstant(block.end) : nowMs,
            y: CONTEXT_AXIS_MIDDLE,
            name: block.name,
            // Carried on the point so the tooltip can state the block's own span rather than the
            // single instant Highcharts reports for a hovered point, and quote what was written
            // about the change that started it.
            custom: { endMs: block.end !== null ? productivityInstant(block.end) : null, remarks: block.remarks ?? [] },
        })),
        dataLabels: contextBandDataLabels(CHART_CONTEXT_BAND_COLORS.feedMix.text),
    };
}

// Notes and water Alarms as two `flags` series (Highcharts stock's own marker idiom, not
// the legacy server-side chart-configuration pattern - these are built client-side from the plain
// two arrays the endpoint returns). Anchored to the Production Group band's own axis at its top
// edge, so the flags read as sitting on the axis rather than competing with any data series -
// they draw whichever bands above are toggled on or off, and whether or not the Section has a
// weight Sensor.
// A Note's pin says what it is with a pencil, and nothing else. The count a cluster stands for goes
// in the tooltip, where it can carry a noun: one glyph slot holding either a symbol or a number is
// unreadable, and a circle containing "2" reads as a measurement.
const NOTE_GLYPH = '✎';

// Alarms are marks, not labels. Drawn as a scatter of small triangles rather than as `flags`,
// because a flag is a filled box with text in it and fifty-four of them across a year is a red
// smear along the top of the chart - loud enough to be the first thing the eye lands on, and
// carrying nothing at that size but "something happened here".
//
// A triangle is the conventional warning mark, reads at 8px, and needs no text. State is the fill:
// solid for an Alarm nobody has explained, hollow for one with a recorded Cause - a distinction
// that survives being small, unlike a tick against an exclamation mark. Density is carried by the
// marks themselves, and how many a mark stands for is in its tooltip.
export const ALARM_SERIES_ID = 'productivityAlarms';
const ALARM_MARKER_MIN_RADIUS = 3.5;
const ALARM_MARKER_MAX_RADIUS = 6;

export function buildEventSeries(notes: NoteMarker[], alarms: AlarmMarker[], spanMs: number) {
    // Notes keep a pin: they are sparse, they carry text a reader opens deliberately, and the pin's
    // stem is what ties that text to a date.
    const notePins = clusterEvents(notes, spanMs, MAX_PIN_EVENTS).map((cluster) => ({
        ...pinAnchor(cluster, NOTE_ROW_Y),
        title: NOTE_GLYPH,
    }));

    // One stream, not one per state. Clustered separately the two would be laid out on independent
    // grids and land on top of each other on the same row - and a mark standing for three Alarms of
    // which one is still unexplained has to read as unexplained, which it cannot do if the handled
    // ones were pulled into a series of their own. So the outstanding case wins the whole cluster,
    // and the fill is set per point rather than per series.
    const alarmMarks = clusterEvents(alarms, spanMs, MAX_ALARM_MARKS).map((cluster) => {
        const unresolved = cluster.some((alarm) => !alarm.handled);
        return {
            ...pinAnchor(cluster, ALARM_ROW_Y),
            marker: {
                // Bigger for a mark standing for more, so density survives clustering as something
                // the eye can see rather than something only the tooltip knows. Capped, because past
                // a point a larger triangle stops reading as "more" and starts reading as "nearer".
                radius: Math.min(ALARM_MARKER_MIN_RADIUS + (cluster.length - 1) * 0.6, ALARM_MARKER_MAX_RADIUS),
                fillColor: unresolved ? CHART_EVENT_COLORS.alarmUnhandled.stroke : CHART_EVENT_COLORS.alarmUnhandled.fill,
                lineColor: unresolved ? CHART_EVENT_COLORS.alarmUnhandled.stroke : CHART_EVENT_COLORS.alarmHandled.stroke,
            },
            // Read back by the tooltip to colour its own swatch, since a per-point marker fill is
            // not something `point.color` reports.
            color: unresolved ? CHART_EVENT_COLORS.alarmUnhandled.stroke : CHART_EVENT_COLORS.alarmHandled.stroke,
        };
    });

    return [
        {
            type: 'flags',
            name: i18n.global.t('views.monitoring.activeGroups.modals.productivity.noteSeries') as string,
            yAxis: PRODUCTIVITY_AXIS.productionGroups,
            y: 0,
            shape: 'circlepin',
            color: CHART_EVENT_COLORS.note.stroke,
            fillColor: CHART_EVENT_COLORS.note.fill,
            lineColor: CHART_EVENT_COLORS.note.stroke,
            style: { color: CHART_EVENT_COLORS.note.text, fontSize: '10px', fontWeight: '700' },
            showInLegend: false,
            data: notePins,
        },
        {
            id: ALARM_SERIES_ID,
            type: 'scatter',
            name: i18n.global.t('views.monitoring.activeGroups.modals.productivity.alarmSeries') as string,
            yAxis: PRODUCTIVITY_AXIS.productionGroups,
            showInLegend: false,
            // Highcharts dims every other series while one point is hovered; on a chart whose whole
            // job is comparing curves, having them all fade because the cursor grazed an Alarm is a
            // worse outcome than no hover feedback at all.
            states: { inactive: { opacity: 1 } },
            marker: { symbol: 'triangle', lineWidth: 1.5, states: { hover: { radiusPlus: 2, lineWidthPlus: 0 } } },
            data: alarmMarks,
        },
    ];
}

// How many pins a chart around a thousand pixels across can carry before they start touching.
// Beyond it they stop being marks on a timeline and become a solid bar that says only "things
// happened", which is exactly what a year of daily Notes produced.
//
// Two numbers because the two marks are not the same width. A Note's 'circlepin' is a fixed ~18px
// circle with a stem; an Alarm triangle is 8-14px and can sit closer to its neighbour without the
// two merging.
const MAX_PIN_EVENTS = 50;
const MAX_ALARM_MARKS = 44;
// How many of a cluster's own labels its tooltip spells out before summarising the remainder. A
// tooltip taller than the band it points at is no more readable than the pins were.
const EVENT_CLUSTER_TOOLTIP_LIMIT = 6;

/**
 * Where a cluster's pin sits, and what its tooltip says. Split from the clustering itself because
 * the two event kinds put different things in the glyph but anchor identically.
 */
// What both marker kinds have in common, which is all the clustering and the pin anchoring ever
// read. Stated structurally rather than as a union of the two, so neither has to be narrowed to be
// laid out.
type TimedMarker = { timestamp: string; label: string };

function pinAnchor(cluster: TimedMarker[], rowY: number): { x: number; y: number; text: string } {
    return {
        // The first event's own instant, not the cluster's midpoint: the pin then sits on something
        // that actually happened rather than on a time at which nothing did.
        x: productivityInstant(cluster[0].timestamp),
        y: rowY,
        text: clusterText(cluster),
    };
}

/**
 * Groups events that would land on top of each other into one cluster apiece. Grouped by elapsed
 * time rather than by pixel because the series is built before the chart it goes into exists -
 * which holds as long as the x axis is linear in time, and it is (`ordinal: false`, set for its own
 * reasons on the chart's x axis).
 */
function clusterEvents<T extends TimedMarker>(events: T[], spanMs: number, maxPins: number): T[][] {
    const clusterMs = spanMs > 0 ? spanMs / maxPins : 0;
    const sorted = [...events].sort((left, right) => productivityInstant(left.timestamp) - productivityInstant(right.timestamp));

    const clusters: T[][] = [];
    for (const event of sorted) {
        const current = clusters[clusters.length - 1];
        const openedAtMs = current ? productivityInstant(current[0].timestamp) : 0;
        if (current && productivityInstant(event.timestamp) - openedAtMs <= clusterMs) current.push(event);
        else clusters.push([event]);
    }

    return clusters;
}

// One entry per line, as its own block rather than a <br/>-joined run: several events under one
// mark are a list, and a list set as one paragraph is where a cluster's tooltip stopped being
// readable. The count leads, subdued and ruled off, so it is read as "how many" rather than as the
// first item.
function clusterEntryHtml(labels: string[]): string {
    return labels.map((label) => `<div style="margin-top:2px">${escapeHtml(label)}</div>`).join('');
}

function clusterText(cluster: TimedMarker[]): string {
    const labels = cluster.map((event) => event.label).filter(Boolean);
    // How many this mark stands for, stated with a noun rather than left as a bare glyph suffix -
    // this is the one place the count can be unambiguous, so it is the place it belongs.
    const header =
        cluster.length > 1
            ? `<div style="color:${colors.gray[400]};font-size:10px;padding-bottom:4px;margin-bottom:4px;border-bottom:1px solid ${colors.gray[100]}">${i18n.global.tc('views.monitoring.activeGroups.modals.productivity.eventClusterCount', cluster.length)}</div>`
            : '';
    if (labels.length <= EVENT_CLUSTER_TOOLTIP_LIMIT) return header + clusterEntryHtml(labels);
    const remainder = labels.length - EVENT_CLUSTER_TOOLTIP_LIMIT;
    const more = i18n.global.t('views.monitoring.activeGroups.modals.productivity.eventClusterMore', { count: remainder }) as string;
    return `${header}${clusterEntryHtml(labels.slice(0, EVENT_CLUSTER_TOOLTIP_LIMIT))}<div style="margin-top:4px;color:${colors.gray[400]}">${more}</div>`;
}
